// src/components/AdminLogSummary.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

const MOBILE_BREAKPOINT = 768;

const MONTH_OPTIONS = [
  { value: "01", label: "Jan" },
  { value: "02", label: "Feb" },
  { value: "03", label: "Mar" },
  { value: "04", label: "Apr" },
  { value: "05", label: "May" },
  { value: "06", label: "Jun" },
  { value: "07", label: "Jul" },
  { value: "08", label: "Aug" },
  { value: "09", label: "Sep" },
  { value: "10", label: "Oct" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Dec" },
];

const AdminLogSummary = () => {
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);

  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false,
  );

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  /* =========================================================
     VIEWPORT WATCHER
  ========================================================= */

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* =========================================================
     FETCH ALL LOGS
  ========================================================= */

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const res = await api.get("/logs/all");

        const data = Array.isArray(res.data) ? res.data : [];

        setLogs(data);
      } catch (e) {
        const status = e?.response?.status || "network";

        const text =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Unknown error";

        setErrorMsg(`Failed to fetch logs (${status}): ${text}`);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  /* =========================================================
     YEAR OPTIONS
  ========================================================= */

  const yearOptions = useMemo(() => {
    const years = new Set();

    logs.forEach((log) => {
      if (!log?.date) return;

      const d = new Date(log.date);

      if (Number.isNaN(d.getTime())) {
        return;
      }

      years.add(String(d.getFullYear()));
    });

    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [logs]);

  /* =========================================================
     AVAILABLE MONTHS FOR SELECTED YEAR
  ========================================================= */

  const availableMonths = useMemo(() => {
    const months = new Set();

    logs.forEach((log) => {
      if (!log?.date) return;

      const d = new Date(log.date);

      if (Number.isNaN(d.getTime())) {
        return;
      }

      if (selectedYear && String(d.getFullYear()) !== String(selectedYear)) {
        return;
      }

      months.add(String(d.getMonth() + 1).padStart(2, "0"));
    });

    return months;
  }, [logs, selectedYear]);

  /* =========================================================
     DEFAULT YEAR + MONTH
  ========================================================= */

  useEffect(() => {
    if (!logs.length || !yearOptions.length || selectedYear) {
      return;
    }

    const validDates = logs
      .map((log) => {
        if (!log?.date) return null;

        const d = new Date(log.date);

        return Number.isNaN(d.getTime()) ? null : d;
      })
      .filter(Boolean)
      .sort((a, b) => b - a);

    if (!validDates.length) return;

    const now = new Date();

    const currentYear = String(now.getFullYear());

    const currentMonth = String(now.getMonth() + 1).padStart(2, "0");

    const hasCurrentMonth = validDates.some((d) => {
      return (
        String(d.getFullYear()) === currentYear &&
        String(d.getMonth() + 1).padStart(2, "0") === currentMonth
      );
    });

    if (hasCurrentMonth) {
      setSelectedYear(currentYear);

      setSelectedMonth(currentMonth);
    } else {
      const latestDate = validDates[0];

      setSelectedYear(String(latestDate.getFullYear()));

      setSelectedMonth(String(latestDate.getMonth() + 1).padStart(2, "0"));
    }
  }, [logs, yearOptions, selectedYear]);

  /* =========================================================
     FILTER LOGS BY YEAR + MONTH
  ========================================================= */

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (!log?.date) {
        return false;
      }

      const d = new Date(log.date);

      if (Number.isNaN(d.getTime())) {
        return false;
      }

      /* YEAR FILTER */

      if (selectedYear && String(d.getFullYear()) !== String(selectedYear)) {
        return false;
      }

      /* MONTH FILTER */

      if (
        selectedMonth &&
        String(d.getMonth() + 1).padStart(2, "0") !== selectedMonth
      ) {
        return false;
      }

      return true;
    });
  }, [logs, selectedYear, selectedMonth]);

  /* =========================================================
     UNIQUE EMPLOYEE MAP
  ========================================================= */

  const employeeMap = useMemo(() => {
    const map = new Map();

    filteredLogs.forEach((log) => {
      const name = log.user?.name || "Unknown";

      const userId = log.user?._id || name;

      map.set(name, userId);
    });

    return new Map([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  }, [filteredLogs]);

  /* =========================================================
     PERIOD LABEL
  ========================================================= */

  const selectedPeriodLabel = useMemo(() => {
    if (!selectedYear) {
      return "All Period";
    }

    if (!selectedMonth) {
      return `Year ${selectedYear}`;
    }

    const monthName =
      MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.label ||
      selectedMonth;

    return `${monthName} ${selectedYear}`;
  }, [selectedYear, selectedMonth]);

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div style={styles.container}>
      <h2 style={styles.pageTitle}>👥 Employee Log Summary</h2>

      {/* FILTER BAR */}

      <div style={styles.topBar}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {/* YEAR */}

          <div style={styles.filterItem}>
            <label style={styles.label}>Year</label>

            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);

                setSelectedMonth("");
              }}
              style={styles.select}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* MONTH */}

          <div style={styles.filterItem}>
            <label style={styles.label}>Month</label>

            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={styles.select}
            >
              <option value="">All Months</option>

              {MONTH_OPTIONS.map((month) => (
                <option
                  key={month.value}
                  value={month.value}
                  disabled={!availableMonths.has(month.value)}
                >
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.periodBadge}>{selectedPeriodLabel}</div>
        </div>

        {errorMsg ? (
          <div style={styles.error}>{errorMsg}</div>
        ) : loading ? (
          <div style={styles.muted}>Loading…</div>
        ) : null}
      </div>

      {/* MOBILE */}

      {isMobile ? (
        <div
          style={{
            display: "grid",
            gap: 8,
          }}
        >
          {[...employeeMap.entries()].map(([name, userId]) => (
            <div key={userId} style={styles.mobileRow}>
              <div
                style={{
                  fontWeight: 600,
                }}
              >
                {name}
              </div>

              <button
                style={styles.smallButton}
                onClick={() => navigate(`/admin/logs/${userId}`)}
              >
                📊 View
              </button>
            </div>
          ))}

          {employeeMap.size === 0 && (
            <div style={styles.empty}>
              No logs found for {selectedPeriodLabel}.
            </div>
          )}
        </div>
      ) : (
        /* DESKTOP */

        <div
          style={{
            overflowX: "auto",
          }}
        >
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Employee</th>

                <th style={styles.th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {[...employeeMap.entries()].map(([name, userId]) => (
                <tr key={userId}>
                  <td style={styles.td}>{name}</td>

                  <td style={styles.td}>
                    <button
                      style={styles.smallButton}
                      onClick={() => navigate(`/admin/logs/${userId}`)}
                    >
                      📊 View Logs
                    </button>
                  </td>
                </tr>
              ))}

              {employeeMap.size === 0 && (
                <tr>
                  <td style={styles.td} colSpan={2}>
                    No logs found for {selectedPeriodLabel}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* =========================================================
   STYLES
========================================================= */

const styles = {
  container: {
    padding: 16,
    background: "#f4f6fb",
    minHeight: "100%",
  },

  pageTitle: {
    fontSize: 22,
    fontWeight: 800,
    textAlign: "center",
    margin: "6px 0 14px 0",
    color: "#2c2f6b",
  },

  topBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    justifyContent: "space-between",
    marginBottom: 10,
    flexWrap: "wrap",
  },

  filterItem: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },

  label: {
    fontSize: 12,
    color: "#374151",
    fontWeight: 700,
  },

  select: {
    height: 36,
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
    minWidth: 140,
  },

  periodBadge: {
    height: 36,
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
    borderRadius: 6,
    background: "#eef2ff",
    color: "#343a76",
    fontSize: 12,
    fontWeight: 700,
    border: "1px solid #d9ddff",
  },

  error: {
    color: "#b00020",
    background: "#fde7ea",
    border: "1px solid #f5c2c7",
    padding: "6px 10px",
    borderRadius: 8,
    fontSize: 12,
  },

  muted: {
    color: "#6b7280",
    fontSize: 12,
  },

  mobileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #e5e7eb",
    background: "#fafbff",
    padding: 10,
    borderRadius: 10,
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 420,
    background: "#fff",
    border: "1px solid #eaecef",
    borderRadius: 12,
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },

  th: {
    padding: 10,
    textAlign: "center",
    position: "sticky",
    top: 0,
    zIndex: 1,
    background: "#f8fafc",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 700,
    color: "#111827",
  },

  td: {
    padding: 10,
    borderBottom: "1px solid #f1f3f5",
    textAlign: "center",
    fontSize: 14,
  },

  smallButton: {
    padding: "6px 10px",
    backgroundColor: "#343a76",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
  },

  empty: {
    padding: 14,
    textAlign: "center",
    border: "1px dashed #d1d5db",
    borderRadius: 10,
    background: "#fff",
    color: "#6b7280",
  },
};

export default AdminLogSummary;
